/**
 * Face geometry: canonical model, rotations, and pinhole projection.
 *
 * The coordinate frame throughout is the standard camera frame: **x right, y down, z away
 * from the camera.** Model points are in millimetres from a widely used anthropometric
 * approximation; absolute scale is irrelevant because only ratios affect the angles.
 *
 * Everything here is pure arithmetic with no dependencies, which is what allows the pose
 * maths to be verified numerically in a unit test rather than trusted.
 */

import { headPose, type HeadPose } from '../models.js';

export type Vec2 = readonly [number, number];
export type Vec3 = readonly [number, number, number];
export type Mat3 = readonly [Vec3, Vec3, Vec3];

export const MODEL_POINT_ORDER = [
  'noseTip',
  'chin',
  'eyeImageLeft',
  'eyeImageRight',
  'mouthImageLeft',
  'mouthImageRight',
] as const;
export type ModelPointName = (typeof MODEL_POINT_ORDER)[number];

/**
 * Canonical face in camera coordinates.
 *
 * The nose tip is the origin and the closest point to the camera; eyes, mouth and chin sit
 * further away (positive z), eyes above the nose (negative y), chin below it (positive y).
 */
export const CANONICAL_MODEL_POINTS: Readonly<Record<ModelPointName, Vec3>> = {
  noseTip: [0, 0, 0],
  chin: [0, 330, 65],
  eyeImageLeft: [-225, -170, 135],
  eyeImageRight: [225, -170, 135],
  mouthImageLeft: [-150, 150, 125],
  mouthImageRight: [150, 150, 125],
};

export const MODEL_POINTS: readonly Vec3[] = MODEL_POINT_ORDER.map(
  (name) => CANONICAL_MODEL_POINTS[name],
);

/**
 * Vertical span of the model between eye line and chin, used to seed the initial depth
 * estimate for the solver.
 */
export const MODEL_EYE_TO_CHIN_MM =
  CANONICAL_MODEL_POINTS.chin[1] - CANONICAL_MODEL_POINTS.eyeImageLeft[1];

/**
 * MediaPipe Face Landmarker (468/478-point mesh) indices.
 *
 * Eye and mouth landmarks are given as *unordered pairs* on purpose. Numbering schemes
 * disagree about whether "left" means the subject's left or the image's left, and a mirrored
 * capture inverts it again. The estimator resolves sides from measured image x-order
 * instead, so a remembered convention cannot silently mirror every result.
 */
export const MEDIAPIPE_LANDMARK_INDICES = {
  noseTip: 1,
  chin: 152,
  eyeOuterPair: [33, 263] as const,
  mouthCornerPair: [61, 291] as const,
} as const;

export type LandmarkIndexMap = typeof MEDIAPIPE_LANDMARK_INDICES;

// ---------------------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------------------

export interface CameraIntrinsics {
  readonly focalLength: number;
  readonly centreX: number;
  readonly centreY: number;
}

/**
 * Pinhole intrinsics assumed from frame size.
 *
 * Without a per-device calibration the focal length is approximated as the frame width,
 * corresponding to roughly a 60° horizontal field of view — typical for a laptop webcam.
 * `focalLengthScale` lets a device profile correct this; getting it badly wrong mostly
 * scales the apparent angles, which is precisely the error per-employee calibration absorbs.
 */
export function buildIntrinsics(
  width: number,
  height: number,
  focalLengthScale = 1,
): CameraIntrinsics {
  return {
    focalLength: width * focalLengthScale,
    centreX: width / 2,
    centreY: height / 2,
  };
}

// ---------------------------------------------------------------------------------------
// Rotations
// ---------------------------------------------------------------------------------------

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;

/** Map an angle to (-180, 180]. */
export function wrapDegrees(value: number): number {
  let wrapped = (value + 180) % 360;
  if (wrapped <= 0) wrapped += 360;
  return wrapped - 180;
}

/**
 * Build a rotation matrix from the project's yaw/pitch/roll convention.
 *
 * Inverse of {@link eulerFromRotationMatrix}. A positive rotation about the camera y-axis
 * swings the nose toward image-right, i.e. the subject turning to their own left, which the
 * employee-centric convention calls a negative yaw — hence the sign on `y`.
 */
export function rotationMatrixFromEuler(pose: HeadPose): Mat3 {
  const x = -pose.pitch * RAD;
  const y = pose.yaw * RAD;
  const z = -pose.roll * RAD;

  const [sx, cx] = [Math.sin(x), Math.cos(x)];
  const [sy, cy] = [Math.sin(y), Math.cos(y)];
  const [sz, cz] = [Math.sin(z), Math.cos(z)];

  // Rz @ Ry @ Rx, expanded.
  return [
    [cz * cy, cz * sy * sx - sz * cx, cz * sy * cx + sz * sx],
    [sz * cy, sz * sy * sx + cz * cx, sz * sy * cx - cz * sx],
    [-sy, cy * sx, cy * cx],
  ];
}

/**
 * Decompose a rotation matrix into the project's yaw/pitch/roll convention.
 *
 * Standard ZYX (Tait-Bryan) extraction, mapped onto the documented product convention.
 * Asserted against synthetic projections of known rotations in `test/headPose.test.ts` for
 * all three axes, because a mirrored convention would round-trip perfectly while silently
 * reversing every direction statistic the product reports.
 */
export function eulerFromRotationMatrix(r: Mat3): HeadPose {
  const sy = Math.hypot(r[0][0], r[1][0]);
  let ax: number;
  let ay: number;
  let az: number;

  if (sy > 1e-6) {
    ax = Math.atan2(r[2][1], r[2][2]);
    ay = Math.atan2(-r[2][0], sy);
    az = Math.atan2(r[1][0], r[0][0]);
  } else {
    // Gimbal-locked: roll is indeterminate, fold it into yaw.
    ax = Math.atan2(-r[1][2], r[1][1]);
    ay = Math.atan2(-r[2][0], sy);
    az = 0;
  }

  return headPose(wrapDegrees(ay * DEG), wrapDegrees(-ax * DEG), wrapDegrees(-az * DEG));
}

/** Rodrigues: rotation vector (axis * angle, radians) to rotation matrix. */
export function rotationMatrixFromVector(v: Vec3): Mat3 {
  const theta = Math.hypot(v[0], v[1], v[2]);
  if (theta < 1e-12) {
    return [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
    ];
  }
  const [kx, ky, kz] = [v[0] / theta, v[1] / theta, v[2] / theta];
  const s = Math.sin(theta);
  const c = Math.cos(theta);
  const t = 1 - c;

  return [
    [t * kx * kx + c, t * kx * ky - s * kz, t * kx * kz + s * ky],
    [t * kx * ky + s * kz, t * ky * ky + c, t * ky * kz - s * kx],
    [t * kx * kz - s * ky, t * ky * kz + s * kx, t * kz * kz + c],
  ];
}

/** Inverse Rodrigues: rotation matrix to rotation vector. */
export function rotationVectorFromMatrix(r: Mat3): Vec3 {
  const trace = r[0][0] + r[1][1] + r[2][2];
  const cos = Math.max(-1, Math.min(1, (trace - 1) / 2));
  const theta = Math.acos(cos);
  if (theta < 1e-12) return [0, 0, 0];

  const sin = Math.sin(theta);
  if (Math.abs(sin) < 1e-9) {
    // theta close to pi: use the symmetric part for numerical stability.
    const xx = Math.sqrt(Math.max(0, (r[0][0] + 1) / 2));
    const yy = Math.sqrt(Math.max(0, (r[1][1] + 1) / 2));
    const zz = Math.sqrt(Math.max(0, (r[2][2] + 1) / 2));
    return [theta * xx, theta * yy, theta * zz];
  }

  const factor = theta / (2 * sin);
  return [
    factor * (r[2][1] - r[1][2]),
    factor * (r[0][2] - r[2][0]),
    factor * (r[1][0] - r[0][1]),
  ];
}

export function multiplyMatrixVector(m: Mat3, v: Vec3): Vec3 {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
  ];
}

// ---------------------------------------------------------------------------------------
// Projection
// ---------------------------------------------------------------------------------------

/** Project 3D model points through a rotation, translation and pinhole camera. */
export function projectPoints(
  points: readonly Vec3[],
  rotation: Mat3,
  translation: Vec3,
  intrinsics: CameraIntrinsics,
): Vec2[] {
  const projected: Vec2[] = [];
  for (const point of points) {
    const rotated = multiplyMatrixVector(rotation, point);
    const z = rotated[2] + translation[2];
    // Guard against a degenerate solution putting a point at or behind the pinhole.
    const depth = Math.abs(z) < 1e-6 ? (z < 0 ? -1e-6 : 1e-6) : z;
    projected.push([
      ((rotated[0] + translation[0]) / depth) * intrinsics.focalLength + intrinsics.centreX,
      ((rotated[1] + translation[1]) / depth) * intrinsics.focalLength + intrinsics.centreY,
    ]);
  }
  return projected;
}

/**
 * Project the canonical face model under a known rotation.
 *
 * This is the ground-truth generator for the head-pose tests: rotate the model by an exact
 * angle, project it through the same pinhole camera the estimator assumes, and require the
 * estimator to recover the angle. It is also what `selfTestPoseSolver` uses to validate the
 * CV maths on a machine with no camera.
 */
export function projectModelPoints(
  pose: HeadPose,
  options: {
    imageWidth: number;
    imageHeight: number;
    distanceMm?: number;
    focalLengthScale?: number;
    offsetPx?: Vec2;
  },
): Vec2[] {
  const {
    imageWidth,
    imageHeight,
    distanceMm = 700,
    focalLengthScale = 1,
    offsetPx = [0, 0],
  } = options;
  const intrinsics = buildIntrinsics(imageWidth, imageHeight, focalLengthScale);
  const rotation = rotationMatrixFromEuler(pose);
  return projectPoints(MODEL_POINTS, rotation, [offsetPx[0], offsetPx[1], distanceMm], intrinsics);
}

/** 2D endpoints for drawing the head's local axes in a preview overlay. */
export function poseAxisEndpoints(
  pose: HeadPose,
  origin: Vec2,
  lengthPx = 80,
): Record<'x' | 'y' | 'z', Vec2> {
  const rotation = rotationMatrixFromEuler(pose);
  const axes: Record<'x' | 'y' | 'z', Vec3> = {
    x: [1, 0, 0],
    y: [0, 1, 0],
    z: [0, 0, -1], // forward, toward the camera
  };
  const result = {} as Record<'x' | 'y' | 'z', Vec2>;
  for (const key of ['x', 'y', 'z'] as const) {
    const rotated = multiplyMatrixVector(rotation, axes[key]);
    result[key] = [origin[0] + rotated[0] * lengthPx, origin[1] + rotated[1] * lengthPx];
  }
  return result;
}

/** Mean pose of a tight cluster — only meaningful for calibration steps. */
export function meanPose(poses: readonly HeadPose[]): HeadPose | null {
  if (poses.length === 0) return null;
  const count = poses.length;
  let yaw = 0;
  let pitch = 0;
  let roll = 0;
  for (const pose of poses) {
    yaw += pose.yaw;
    pitch += pose.pitch;
    roll += pose.roll;
  }
  return headPose(yaw / count, pitch / count, roll / count);
}
